import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Input, Modal, Spin } from 'antd';
import dayjs from 'dayjs';
import AlertBox from '@/components/alert-box';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';
import { DATE_FORMATS } from '@/utils/formUtils';
import { isValidEmail, terminationQueryKey } from './terminationShared';

/**
 * "Notify" about a termination: the recipients offered by the company's rule
 * (its HR and the employee's sector HR ticked, the rest listed unticked), a
 * few extra addresses, and the notices sent so far. The email carries no
 * attachment — the document stays on the platform.
 */
export default function TerminationNotifyDialog({ employeeId, open, onClose }) {
  const { t } = useTranslation(['translations']);
  const tt = (key, opts) =>
    t(`terminationUi.${key}`, { ns: 'translations', ...opts });
  const notification = useNotify();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: terminationQueryKey(employeeId),
    queryFn: () => API.employees.getCurrentTermination(employeeId),
    enabled: Boolean(open && employeeId),
    retry: false,
  });
  const termination = data?.termination || null;
  const offered = useMemo(() => termination?.recipients || [], [termination]);
  const maxExtra = termination?.maxExtraRecipients || 10;

  const [checked, setChecked] = useState([]);
  const [extra, setExtra] = useState([]);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState('');

  // Starts from the offered defaults each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setChecked(offered.filter((r) => r.checked).map((r) => r.email));
    setExtra([]);
    setDraft('');
    setDraftError('');
  }, [open, offered]);

  const addDraft = () => {
    const email = draft.trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) {
      setDraftError(tt('invalidEmail'));
      return;
    }
    if (offered.some((r) => r.email === email) || extra.includes(email)) {
      setChecked((prev) => (prev.includes(email) ? prev : [...prev, email]));
      setDraft('');
      setDraftError('');
      return;
    }
    if (extra.length >= maxExtra) {
      setDraftError(tt('tooManyExtra', { n: maxExtra }));
      return;
    }
    setExtra((prev) => [...prev, email]);
    setChecked((prev) => [...prev, email]);
    setDraft('');
    setDraftError('');
  };

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      API.employees.notifyTermination(employeeId, {
        contractId: termination.contractId,
        recipients: checked,
      }),
    onSuccess: (response) => {
      notification.success({ message: response?.message || tt('notified') });
      queryClient.invalidateQueries({
        queryKey: terminationQueryKey(employeeId),
      });
      onClose();
    },
    onError: (error) => {
      notification.error({
        message:
          error?.response?.data?.message ||
          error?.message ||
          tt('notifyFailed'),
      });
    },
  });

  const sourceLabel = (source) =>
    source === 'employee'
      ? tt('sourceEmployee')
      : source === 'companyHr'
        ? tt('sourceCompanyHr')
        : source === 'sectorHr'
          ? tt('sourceSectorHr')
          : tt('sourceOther');

  const reasonText = termination?.reason
    ? termination.reason === 'OTHER' && termination.reasonNote
      ? `${tt(`reasons.${termination.reason}`)}: ${termination.reasonNote}`
      : tt(`reasons.${termination.reason}`)
    : '—';

  const rows = [
    ...offered.map((r) => ({ email: r.email, label: sourceLabel(r.source) })),
    ...extra.map((email) => ({ email, label: tt('sourceExtra') })),
  ];

  return (
    <Modal
      open={open}
      title={tt('notifyTitle')}
      onCancel={onClose}
      destroyOnHidden
      width={620}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            className="btn-default btn-default--hover !rounded-[50px]"
            onClick={onClose}
          >
            {tt('cancel')}
          </Button>
          <Button
            type="primary"
            icon={<MailOutlined />}
            className="!rounded-[50px]"
            loading={isPending}
            disabled={!termination || checked.length === 0}
            onClick={() => mutate()}
          >
            {tt('send')}
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="grid place-items-center py-8">
          <Spin />
        </div>
      ) : !termination ? (
        <AlertBox variant="warning" className="p-2">
          {tt('noTermination')}
        </AlertBox>
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-1 rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-[12.5px]">
            <span>
              <span className="text-zinc-500">{tt('lastWorkingDay')}: </span>
              <b>
                {termination.endDate
                  ? dayjs(termination.endDate).format(DATE_FORMATS.DISPLAY)
                  : '—'}
              </b>
            </span>
            <span>
              <span className="text-zinc-500">{tt('reason')}: </span>
              <b>{reasonText}</b>
            </span>
            <span>
              <span className="text-zinc-500">{tt('document')}: </span>
              <b>
                {termination.documents?.length
                  ? termination.documents.map((d) => d.title).join(', ')
                  : tt('noDocument')}
              </b>
            </span>
          </div>

          <AlertBox variant="info" className="p-2 text-[12px]">
            {tt('noAttachmentInfo')}
          </AlertBox>

          <div className="grid gap-2">
            <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
              {tt('recipients')}
            </span>
            {rows.length ? (
              <Checkbox.Group
                value={checked}
                onChange={setChecked}
                className="grid gap-1.5"
              >
                {rows.map((row) => (
                  <Checkbox key={row.email} value={row.email}>
                    <span className="text-[13px] text-zinc-800">
                      {row.email}
                    </span>
                    <span className="ml-2 text-[11.5px] text-zinc-400">
                      {row.label}
                    </span>
                  </Checkbox>
                ))}
              </Checkbox.Group>
            ) : (
              <span className="text-[12.5px] text-zinc-400">
                {tt('noOfferedRecipients')}
              </span>
            )}
            <div className="flex gap-2">
              <Input
                value={draft}
                status={draftError ? 'error' : undefined}
                placeholder={tt('addEmailPlaceholder')}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setDraftError('');
                }}
                onPressEnter={addDraft}
              />
              <Button
                className="btn-default btn-default--hover !rounded-[50px]"
                onClick={addDraft}
              >
                {tt('addEmail')}
              </Button>
            </div>
            {draftError && (
              <span className="text-[12px] text-red-600">{draftError}</span>
            )}
          </div>

          {termination.notifications?.length > 0 && (
            <div className="grid gap-1">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
                {tt('sentNotices')}
              </span>
              {termination.notifications.map((n) => (
                <span
                  key={`${n.sentAt}-${n.recipients?.join(',')}`}
                  className="text-[12px] text-zinc-600"
                >
                  {dayjs(n.sentAt).format(`${DATE_FORMATS.DISPLAY} HH:mm`)}
                  {n.sentByName ? ` · ${n.sentByName}` : ''} ·{' '}
                  {n.recipients?.join(', ')}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
